import React, { useState, useEffect, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { Input, Spin } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';

export interface SearchBarProps {
  placeholder?: string;
  label?: string;
  onSearchChange: (value: string) => void;
  debounceMs?: number;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search...',
  label,
  onSearchChange,
  debounceMs = 300,
  className = '',
  disabled = false,
  loading = false,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [previousSearchValue, setPreviousSearchValue] = useState('');

  // Create a debounced version of onSearchChange
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      const trimmedValue = value.trim();
      setIsSearching(false);
      if (trimmedValue.length >= 2) {
        onSearchChange(trimmedValue);
        setPreviousSearchValue(trimmedValue);
      } else {
        if (previousSearchValue !== trimmedValue) {
          onSearchChange('');
        }
        setPreviousSearchValue('');
      }
    }, debounceMs),
    [onSearchChange, debounceMs]
  );

  // Update the debounced search when searchValue changes
  useEffect(() => {
    if (searchValue.trim().length >= 2) {
      setIsSearching(true);
    }
    debouncedSearch(searchValue);

    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchValue, debouncedSearch]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  return (
    <div className={`p-0 m-0 ${className}`} style={{ width: '100%' }}>
      {label && <div className="search-label">{label}</div>}
      <Input
        id="search-input"
        value={searchValue}
        onChange={handleChange}
        placeholder={placeholder}
        prefix={<SearchOutlined />}
        disabled={disabled}
        suffix={loading || isSearching ? <Spin size="small" /> : null}
        allowClear={searchValue.length > 0}
        style={{ width: '100%' }}
      />
    </div>
  );
};

export default React.memo(SearchBar);
